import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import Button from 'src/components/controls/forms/redesign/FormButton';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { ODataOptions } from 'src/util/ODataBuilder';
import { useInfiniteScroll, IPagedResult } from 'src/util/useInfiniteScroll';
import { useFillRemainingHeight } from 'src/util/useFillRemainingHeight';
import ICostBreakdownJob from '../models/costBreakdownJob';
import ICostBreakdownJobProspect from '../models/costBreakdownJobProspect';
import { scheduleDescription } from './scheduleDescription';

interface ICostBreakdownJobsViewProps {
    onBack: () => void;
    onAddJob: () => void;
    onEditJob: (job: ICostBreakdownJob) => void;
    refreshKey: number;
}

const PAGE_SIZE = 50;

const formatDate = (value?: string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
};

// Mirrors the Jobs window - a master grid of scheduled jobs with a detail grid on the right showing
// the prospects attached to whichever job is selected (COST_BREAKDOWN_JOB_PROSPECT), same
// master-detail idea as Oracle's own canvas. Add Job/Edit Job open the same dialog (owned by the
// parent page, same pattern as Add Report - it shares the single FindClientDialog instance, whose
// state needs to survive this view swapping in/out).
const CostBreakdownJobsView = (props: ICostBreakdownJobsViewProps) => {
    const { get: getProspects, response: prospectsResponse } = useFetch();
    const [selectedJobId, setSelectedJobId] = useState<number | undefined>(undefined);
    const [jobProspects, setJobProspects] = useState<Array<ICostBreakdownJobProspect>>([]);
    const [loadingProspects, setLoadingProspects] = useState<boolean>(false);

    const { get: getJobs, response: jobsResponse } = useFetch();

    const jobs = useInfiniteScroll<ICostBreakdownJob>(async (pageNumber): Promise<IPagedResult<ICostBreakdownJob>> => {
        const options = new ODataOptions().addPaging(pageNumber, PAGE_SIZE);
        const result = await getJobs(`/v1.0/CostBreakdown/jobs${options.toString()}`);
        if (!jobsResponse.ok) {
            return { items: [], totalRecords: 0 };
        }
        return { items: result?.items ?? [], totalRecords: result?.totalRecords ?? 0 };
    }, PAGE_SIZE);

    useEffect(() => {
        jobs.reset();
        jobs.loadNextPage();
        setSelectedJobId(undefined);
        setJobProspects([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refreshKey]);

    useEffect(() => {
        if (selectedJobId === undefined) {
            setJobProspects([]);
            return;
        }
        setLoadingProspects(true);
        (async () => {
            const result = await getProspects(`/v1.0/CostBreakdown/jobs/${selectedJobId}/prospects`);
            setJobProspects(prospectsResponse.ok && Array.isArray(result) ? result : []);
            setLoadingProspects(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedJobId]);

    const selectedJob = jobs.items.find((j) => j.id === selectedJobId);
    const masterGrid = useFillRemainingHeight<HTMLDivElement>(32);
    const detailGrid = useFillRemainingHeight<HTMLDivElement>(32);

    return (
        <div>
            <div style={{ marginBottom: '.5rem' }}>
                <Button
                    text="Show Reports"
                    style={{ background: 'none', border: 'none', padding: 0, color: '#6393C1', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => props.onBack()}
                />
            </div>
            <div className="form-row" style={{ marginBottom: '.5rem', alignItems: 'center' }}>
                <Button className="blue-hover-button" style={{ height: '3rem', marginRight: '.5rem' }} text="Add Job" onClick={() => props.onAddJob()} />
                <Button
                    className="blue-hover-button"
                    style={{ height: '3rem' }}
                    text="Edit Job"
                    disabled={selectedJob === undefined}
                    onClick={() => selectedJob && props.onEditJob(selectedJob)}
                />
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: '2 1 0' }} ref={masterGrid.ref}>
                    <div style={{ overflowY: 'scroll', height: masterGrid.height, border: '1px solid #ccc' }} onScroll={jobs.onScroll}>
                        <table style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>Job ID</th><th>Cl. No.</th><th style={{ textAlign: 'left' }}>Email To</th>
                                    <th style={{ textAlign: 'left' }}>CC</th><th>Create By</th><th>Create Date</th>
                                    <th style={{ textAlign: 'left' }}>Scheduled</th>
                                </tr>
                            </thead>
                            <tbody>
                                {jobs.loading && jobs.items.length === 0 && (
                                    <tr><td colSpan={7} className="bold-centered"><LoadingSpinner /></td></tr>
                                )}
                                {!jobs.loading && jobs.items.length === 0 && (
                                    <tr><td colSpan={7} className="bold-centered">No Jobs Found</td></tr>
                                )}
                                {jobs.items.map((job) => (
                                    <tr
                                        key={job.id}
                                        className="table-item-hover"
                                        style={{ cursor: 'pointer', backgroundColor: job.id === selectedJobId ? '#FFFF66' : '' }}
                                        onClick={() => setSelectedJobId(job.id === selectedJobId ? undefined : job.id)}
                                    >
                                        <td>{job.id}</td><td>{job.clNo}</td><td style={{ textAlign: 'left' }}>{job.email}</td>
                                        <td style={{ textAlign: 'left' }}>{job.cc}</td><td>{job.createBy}</td><td>{formatDate(job.createDate)}</td>
                                        <td style={{ textAlign: 'left' }}>{scheduleDescription(job.frequency, job.dayNo)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div style={{ flex: '1 1 0' }} ref={detailGrid.ref}>
                    <div style={{ overflowY: 'scroll', height: detailGrid.height, border: '1px solid #ccc' }}>
                        <table style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>Cl. No.</th><th>Pr. No.</th><th style={{ textAlign: 'left' }}>Project</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedJobId === undefined && (
                                    <tr><td colSpan={3} className="bold-centered">Select a job to see its prospects.</td></tr>
                                )}
                                {selectedJobId !== undefined && loadingProspects && (
                                    <tr><td colSpan={3} className="bold-centered"><LoadingSpinner /></td></tr>
                                )}
                                {selectedJobId !== undefined && !loadingProspects && jobProspects.length === 0 && (
                                    <tr><td colSpan={3} className="bold-centered">No Prospects Found</td></tr>
                                )}
                                {jobProspects.map((p, index) => (
                                    <tr key={`${p.clNo}-${p.prNo}-${index}`}>
                                        <td>{p.clNo}</td><td>{p.prNo}</td><td style={{ textAlign: 'left' }}>{p.prName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CostBreakdownJobsView;



// WEBPACK FOOTER //
// ./src/components/project-billing/components/CostBreakdownJobsView.tsx